import React, { useEffect } from "react";
import { cn } from "@udecode/cn";
import { PlateContent, focusEditor, useEditorRef } from "@udecode/plate-common";
import { cva } from "class-variance-authority";

import type { PlateContentProps } from "@udecode/plate-common";
import type { VariantProps } from "class-variance-authority";
import { Box, Fab } from "@mui/material";
import { Icons } from "./Icons/icons";
import { alpha } from "@mui/material";

const editorVariants = cva(
  cn(
    "relative overflow-x-auto whitespace-pre-wrap break-words",
    "min-h-[80px] w-full rounded-b-md bg-transparent px-10 py-2 text-sm ring-offset-background  focus-visible:outline-none",
    "[&_[data-slate-placeholder]]:!opacity-30",
    "[&_[data-slate-placeholder]]:top-[auto_!important]",
    "[&_strong]:font-bold",
  ),
  {
    variants: {
      variant: {
        outline: "border border-input border-t-0 border-[#0000001f]",
        ghost: "",
      },
      focused: {
        true: "ring-2 ring-ring ring-offset-2",
      },
      disabled: {
        true: "cursor-not-allowed opacity-50",
      },
      focusRing: {
        true: "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        false: "",
      },
      size: {
        sm: "text-sm",
        md: "text-base",
      },
    },
    defaultVariants: {
      variant: "outline",
      focusRing: false,
      size: "sm",
    },
  },
);

export type EditorProps = PlateContentProps &
  VariantProps<typeof editorVariants> &
  AdditionalEditorProps;

type AdditionalEditorProps = {
  // TODO_Editor add more props
};

const Editor = React.forwardRef<HTMLDivElement, EditorProps>(
  (
    {
      className,
      disabled,
      focused,
      focusRing,
      readOnly,
      size,
      variant,
      ...props
    },
    ref,
  ) => {
    const editor = useEditorRef();

    useEffect(() => {
      if (readOnly) {
        return;
      }

      setTimeout(() => {
        focusEditor(editor);
      }, 0);
    }, []);

    return (
      <div ref={ref} className="relative w-full plate-editor">
        <PlateContent
          className={cn(
            editorVariants({
              disabled,
              focused,
              focusRing,
              size,
              variant,
            }),
            className,
          )}
          disableDefaultStyles
          readOnly={disabled ?? readOnly}
          aria-disabled={disabled}
          {...props}
        />
      </div>
    );
  },
);
Editor.displayName = "Editor";

export { Editor };
